import { Link } from 'react-router-dom';
import { DownloadSimple, PlayCircle, ArrowRight, Envelope as Mail } from "@phosphor-icons/react";
import { SiteFooter } from '../components/SiteFooter';
import { SiteNav } from '../components/SiteNav';

const INK = '#2A1E5C';
const GOLD = '#F5CB5C';
const RED = '#DE3C4B';
const CREAM = '#FDFBF7';
const PAPER = '#FAF1D8';
const MUTED = '#5D5A6B';

const DEMO_VIDEO_URL = 'https://customer-assets-rejwkqb3.emergentagent.net/wingman/5cc0ad01-9dd9-4016-b0aa-c22bc26dbf56/attachments/cc7eb30f725d41c49e26a63da2c99c29_payday_relay_explainer_v6.mp4';
const PRESS_EMAIL = 'hello@paydayrelay.com';

const LOGOS = [
  { src: '/images/paydayrelaylogo.png', label: 'Primary logo (color)', file: 'payday-relay-logo-color.png' },
  { src: '/images/paydayrelay-all-white.png', label: 'Logo (white / reverse)', file: 'payday-relay-logo-white.png', dark: true },
  { src: '/images/brand-icon.png', label: 'Baton icon mark', file: 'payday-relay-icon.png' },
];

const FOUNDER_PHOTOS = [
  { src: '/images/founder-dove.jpg', label: 'Shari Dove Shepard — founder portrait', file: 'shari-dove-shepard.jpg' },
  { src: '/images/founder-dove-hat.jpg', label: 'Shari Dove Shepard — outdoors', file: 'shari-dove-shepard-outdoors.jpg' },
];

const PRODUCT_POINTS = [
  'A guided calculator turns a yearly goal into one weekly target, and a daily focus number.',
  'A companion app and a Google Sheet show what you have earned this relay, and when you can stop.',
  'Set-asides for taxes, PTO, debt, and savings stay visible as you log each week.',
  'Built for freelancers, contractors, creators, and platform workers with unstructured income.',
];

const AssetCard = ({ asset, tall }) => (
  <a
    href={asset.src}
    download={asset.file}
    className="group block rounded-2xl overflow-hidden transition-transform hover:-translate-y-0.5"
    style={{ background: '#FFFFFF', border: '1px solid rgba(42,30,92,0.1)', boxShadow: '0 4px 18px rgba(42,30,92,0.06)' }}
    data-testid={`press-download-${asset.file}`}
  >
    <div
      className="flex items-center justify-center p-5"
      style={{ background: asset.dark ? INK : PAPER, aspectRatio: tall ? '3 / 4' : '4 / 3' }}
    >
      <img src={asset.src} alt={asset.label} loading="lazy" className="max-h-full max-w-full object-contain" />
    </div>
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <span className="font-body text-xs leading-snug" style={{ color: INK }}>{asset.label}</span>
      <DownloadSimple className="w-5 h-5 flex-shrink-0 transition-colors" style={{ color: RED }} weight="bold" />
    </div>
  </a>
);

const SectionLabel = ({ children }) => (
  <p className="font-body text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: RED }}>{children}</p>
);

export default function PressKitPage() {
  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="press-kit-page">
      <SiteNav />

      {/* Hero */}
      <section className="pt-14 sm:pt-20 pb-12 px-4 sm:px-6 lg:px-8" style={{ background: INK }}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6" style={{ background: 'rgba(245,203,92,0.2)', color: GOLD }}>
            Press Kit
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-tight mb-4 text-white" data-testid="press-title">
            Payday Relay
          </h1>
          <p className="font-heading text-lg sm:text-2xl" style={{ color: GOLD }} data-testid="press-tagline">
            A finish line for independent work
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-body text-lg sm:text-xl leading-relaxed" style={{ color: INK }} data-testid="press-intro">
            Payday Relay makes "enough" a financial metric. It gives self-employed people a weekly finish
            line that accounts for taxes, rest, and the year ahead, so stopping becomes a calculated decision
            rather than a guilty one.
          </p>
        </div>
      </section>

      {/* About Payday Relay */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8" style={{ background: PAPER }}>
        <div className="max-w-3xl mx-auto">
          <SectionLabel>About Payday Relay</SectionLabel>
          <div className="space-y-4 font-body text-base sm:text-lg leading-relaxed" style={{ color: INK }} data-testid="press-about">
            <p>
              Payday Relay is an early-stage fintech product built for self-employed service providers:
              freelancers, contractors and independent earners who don't have the structure of a regular
              paycheck. Its premise is that freelance income is a weekly race with a visible finish line.
              Earners run toward a weekly target, set aside taxes and time off as they go, and know when
              they're done. Payday Relay sits between personal finance and productivity, and is currently in beta.
            </p>
            <p>A U.S. trademark application for Payday Relay is pending.</p>
          </div>
        </div>
      </section>

      {/* Founder story */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto grid md:grid-cols-[260px_1fr] gap-8 md:gap-12 items-start">
          <div className="mx-auto md:mx-0 w-full max-w-[260px]">
            <img
              src="/images/founder-dove.jpg"
              alt="Shari Dove Shepard, founder of Payday Relay"
              className="w-full h-auto rounded-2xl object-cover"
              style={{ boxShadow: '0 12px 30px rgba(42,30,92,0.18)' }}
              loading="lazy"
              data-testid="press-founder-photo"
            />
            <p className="font-body text-sm font-semibold mt-3 text-center md:text-left" style={{ color: INK }}>
              Shari Dove Shepard
            </p>
            <p className="font-body text-xs text-center md:text-left" style={{ color: MUTED }}>Founder, Payday Relay</p>
          </div>
          <div>
            <SectionLabel>The Founder</SectionLabel>
            <div className="space-y-4 font-body text-base leading-relaxed" style={{ color: INK }} data-testid="press-founder-story">
              <p>
                Founder Shari Dove Shepard spent years trying to succeed inside structures that didn't fit the way
                her brain worked. An ADD diagnosis helped her stop asking why she couldn't keep up and start asking
                what kind of system would actually help. Independent work gave her more freedom, but without a
                predictable paycheck or a clear end to the workday, that freedom could become its own source of pressure.
              </p>
              <p>
                After moving to Mexico, she needed to work toward a concrete financial goal without returning to an
                endless cycle of overwork. She began with a calculator and one number for each week: a relay she
                could run at her own pace. Payday Relay grew from that personal system into a tool for other
                independent earners who want structure around their money, time, and rest.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Link
                to="/founder-story"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-body font-bold text-sm transition-transform hover:scale-[1.02]"
                style={{ background: '#FFFFFF', color: INK, border: `1px solid ${INK}` }}
                data-testid="press-founder-story-link"
              >
                Read the full founder story <ArrowRight className="w-4 h-4" style={{ color: RED }} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Product summary + links */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8" style={{ background: PAPER }}>
        <div className="max-w-3xl mx-auto">
          <SectionLabel>About the product</SectionLabel>
          <ul className="space-y-3 mb-10" data-testid="press-product-summary">
            {PRODUCT_POINTS.map((p) => (
              <li key={p} className="flex items-start gap-3 font-body text-base" style={{ color: INK }}>
                <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: GOLD }} />
                {p}
              </li>
            ))}
          </ul>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={DEMO_VIDEO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-body font-bold text-sm transition-transform hover:scale-[1.02]"
              style={{ background: INK, color: '#FFFFFF' }}
              data-testid="press-demo-video-link"
            >
              <PlayCircle className="w-5 h-5" style={{ color: GOLD }} weight="fill" /> Watch the demo video
            </a>
          </div>
        </div>
      </section>

      {/* Downloads */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium mb-2" style={{ color: INK }}>
            Downloads
          </h2>
          <p className="font-body text-sm mb-10" style={{ color: MUTED }}>
            Approved assets for editorial use. Please don't alter or recolor the logos, and don't imply an
            endorsement or partnership that hasn't been confirmed.
          </p>

          <SectionLabel>Logos</SectionLabel>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12" data-testid="press-logos">
            {LOGOS.map((a) => <AssetCard key={a.file} asset={a} />)}
          </div>

          <SectionLabel>Founder photos</SectionLabel>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="press-founder-downloads">
            {FOUNDER_PHOTOS.map((a) => <AssetCard key={a.file} asset={a} tall />)}
          </div>
        </div>
      </section>

      {/* Media inquiries */}
      <section className="py-14 px-4 sm:px-6 lg:px-8" style={{ background: PAPER }}>
        <div className="max-w-xl mx-auto text-center">
          <SectionLabel>Media inquiries</SectionLabel>
          <h2 className="font-heading text-2xl sm:text-3xl font-medium mb-3" style={{ color: INK }}>
            For journalists, podcast hosts & partners
          </h2>
          <p className="font-body text-base mb-6" style={{ color: MUTED }}>
            Reach us with the subject line "Press inquiry" and we'll get back to you.
          </p>
          <a
            href={`mailto:${PRESS_EMAIL}?subject=Press inquiry`}
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-body font-bold text-sm transition-transform hover:scale-[1.02] shadow-md"
            style={{ background: INK, color: '#FFFFFF' }}
            data-testid="press-contact-link"
          >
            <Mail className="w-5 h-5" style={{ color: GOLD }} /> {PRESS_EMAIL}
          </a>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
